import i18n from "i18next";
import { forwardRef, useState } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { IconX } from "@tabler/icons-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/lilia-ui/popover";
import type { Document } from "@/lib/api";
import { useThemes } from "@/hooks/useThemes";
import { effectiveTheme, normaliseLook } from "@/lib/themes/look";
import { darkPaperFor } from "@/lib/themes/darkPaper";
import { ONCE_HINTS, useOnceHint } from "@/lib/onceHint";
import { useThemeStore } from "@/store/themeStore";
import { MODE_DARK } from "@/constants/theme.constant";
import { testIds } from "@/testIds";
import { computeTypeLabel, themeLabel, type StampDocumentInput } from "./stampText";
import { DocumentStampPopover, type DocumentStampPermission, type DocumentStampStatus } from "./DocumentStampPopover";
import { DocTypeIcon } from "./DocTypeIcon";
import { usePublisherProfiles } from "@/hooks/usePublisherProfiles";
import styles from "./DocumentStamp.module.scss";

export interface DocumentStampProps {
  /**
   * The full Document. We accept the loose api shape and read fields with
   * a structural narrowing in `stampText.resolveOptions`. Missing fields
   * fall back to category defaults — so the stamp renders even before
   * Team 1 / Team 2 finish wiring `latexDocumentClass`, `sides`, etc.
   */
  document: Document;
  /** When true, the popover renders read-only (no permission). */
  readOnly?: boolean;
  /** When true, the popover renders skeletons (initial load). */
  loading?: boolean;
}

/**
 * The stamp is a `<button>` rendered ~8px below the document title.
 *
 * E1: read-only label, no popover.
 * E2-E4: clicking opens the popover with Type & class, Class options,
 *        Layout sections.
 *
 * Visual contract verbatim from Claude Design 02-claude-design-response
 * (round 1) — see /home/oussama/projects/lilia-docs/design-handoffs/
 * 2026-05-06-document-stamp.
 */
export const DocumentStamp = forwardRef<HTMLButtonElement, DocumentStampProps>(
  function DocumentStamp({ document, readOnly = false, loading = false }, ref) {
    // Read the loose document shape into our typed input. Cast through
    // unknown because Document doesn't yet declare every field — Team 1/2
    // own the server-side types. Missing fields fall back to defaults.
    const doc = document as unknown as StampDocumentInput & Document;
    const stampInput: StampDocumentInput = {
      latexDocumentClass: doc.latexDocumentClass,
      documentCategory: doc.documentCategory,
      paperSize: doc.paperSize,
      fontSize: doc.fontSize,
      columns: doc.columns,
      sides: doc.sides,
      titlePage: doc.titlePage,
      orientation: doc.orientation,
    };

    // The stamp surfaces only the document type now — a doc-type icon +
    // the type label. The full class / layout detail lives in the popover.
    // A publisher document says whose ("Article · ACM", 25 Oct 2026).
    const { profiles: publishers } = usePublisherProfiles({ enabled: !!document.publisherProfile });
    const publisher = publishers.find((p) => p.id === document.publisherProfile)?.publisher;
    const typeLabel = publisher ? `${computeTypeLabel(stampInput)} · ${publisher}` : computeTypeLabel(stampInput);
    const ariaLabel = `Document type: ${typeLabel}. Click for document settings.`;

    const status: DocumentStampStatus = loading ? "loading" : "ready";
    const permission: DocumentStampPermission = readOnly ? "readOnly" : "edit";

    // The look the document prints with (REPLY-2026-10-06d §1): "Article ·
    // Carnet", with a swatch of the paper. Classic is the default, so its
    // absence says Classic: the stamp then shows the class only.
    const look = normaliseLook(document.look);
    const themeId = effectiveTheme(document.look, document.lookLocked, document.lookThemes);
    const themed = themeId !== "classic";
    const { themes } = useThemes({ enabled: themed });
    const descriptor = themes.find((t) => t.id === themeId);
    const themeName = themeLabel(themeId, themes);
    const paperColour = look.paper === "white" ? "#FFFFFF" : descriptor?.colours.paper;

    // Opening from the theme name lands on Look, its header focused.
    const [open, setOpen] = useState(false);
    const [focusLook, setFocusLook] = useState(false);
    const onOpenChange = (next: boolean) => {
      setOpen(next);
      if (!next) setFocusLook(false);
    };
    const openAtLook = () => {
      setFocusLook(true);
      setOpen(true);
    };

    // In Dark, a coloured paper shows as its dark-paper pair: say so once.
    const dark = useThemeStore((st) => st.mode) === MODE_DARK;
    const tinted = dark && look.paper === "theme" && !!darkPaperFor(themeId);
    const hint = useOnceHint(ONCE_HINTS.darkPaper, tinted && !loading);

    // A reader who can't change the settings gets the type as text, not a
    // control that opens to read-only rows (Olivia, 10 Oct).
    if (readOnly) {
      return (
        <div className={styles.stampHost} data-testid="document-stamp-host" data-permission={permission}>
          <span className={`${styles.stamp} ${styles.stampPlain}`} data-testid="document-stamp" aria-label={`Document type: ${typeLabel}`}>
            <DocTypeIcon size={14} />
            <span className={styles.cls}>{typeLabel}</span>
            {themed && <span className={styles.themeName}>· {themeName}</span>}
          </span>
        </div>
      );
    }

    return (
      <div className={styles.stampHost} data-testid="document-stamp-host" data-permission={permission}>
        <Popover open={open} onOpenChange={onOpenChange}>
          <PopoverPrimitive.Anchor asChild>
            <div className={styles.stampChip}>
              <PopoverTrigger asChild>
                <button
                  ref={ref}
                  type="button"
                  className={`${styles.stamp} ${themed ? styles.stampJoined : ""}`}
                  aria-label={ariaLabel}
                  data-testid="document-stamp"
                >
                  <DocTypeIcon size={14} />
                  <span className={styles.cls}>{typeLabel}</span>
                  {!themed && (
                    <span className={styles.chev} aria-hidden="true">
                      ▾
                    </span>
                  )}
                </button>
              </PopoverTrigger>
              {themed && (
                <button
                  type="button"
                  className={`${styles.stamp} ${styles.stampLook}`}
                  aria-label={`Look: ${themeName}. Open Look in document settings.`}
                  aria-haspopup="dialog"
                  aria-expanded={open && focusLook}
                  data-testid={testIds.look.stampLook}
                  data-theme={themeId}
                  onClick={openAtLook}
                >
                  <span className={styles.dot} aria-hidden="true">
                    ·
                  </span>
                  {paperColour && (
                    <span
                      className={styles.swatch}
                      style={{ background: paperColour }}
                      data-paper-colour={paperColour}
                      aria-hidden="true"
                    />
                  )}
                  <span className={styles.themeName}>{themeName}</span>
                  <span className={styles.chev} aria-hidden="true">
                    ▾
                  </span>
                </button>
              )}
            </div>
          </PopoverPrimitive.Anchor>
          <PopoverContent
            align="start"
            sideOffset={42 - 22 /* 42px below stamp top → 20px below stamp bottom */}
            className={styles.popover}
            data-testid="document-stamp-popover"
            onOpenAutoFocus={(e) => {
              // Look's header takes focus instead of the first control.
              if (focusLook) e.preventDefault();
            }}
          >
            <DocumentStampPopover
              document={document}
              status={status}
              permission={permission}
              focusLook={focusLook}
            />
          </PopoverContent>
        </Popover>
        {hint.visible && (
          <div className={styles.darkPaperHint} role="status" data-testid={testIds.look.darkPaperHint}>
            <span>{i18n.t("editor:stamp.shownDarkerInDarkMode")}</span>
            <button
              type="button"
              className={styles.darkPaperHintDismiss}
              aria-label={i18n.t("editor:stamp.dismiss")}
              data-testid={testIds.look.darkPaperHintDismiss}
              onClick={hint.dismiss}
            >
              <IconX size={12} aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
    );
  },
);
